/**
 * Custom Hook: useScriptMappings
 *
 * Fetches script action mappings from DynamoDB (only source of truth)
 * No hard-coded fallbacks - must load from database
 *
 * Usage:
 * const { mappings, loading, error } = useScriptMappings();
 * if (loading) return <CircularProgress />;
 * if (error) return <Alert severity="error">{error.message}</Alert>;
 * // Use mappings - guaranteed to be from DynamoDB
 */

import { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import { getAllScriptMappings } from '../graphql/scriptPathQueries';
import { transformDynamoDBToActions } from '../utils/scriptActionMappings';

export function useScriptMappings() {
  const [mappings, setMappings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMappings = async () => {
      setLoading(true);
      setError(null);

      try {
        const client = generateClient();
        const response = await client.graphql({
          query: getAllScriptMappings
        });

        if (!response.data?.listScriptPathMappings?.items) {
          throw new Error('No script mappings found in database');
        }

        const items = response.data.listScriptPathMappings.items;

        items.sort(function(a, b) {
            const keyA = (a.action_key || "").toLowerCase();
            const keyB = (b.action_key || "").toLowerCase();

            if (keyA < keyB) return -1;
            if (keyA > keyB) return 1;
            return 0;
        });

        // Add "None" option manually (not stored in DB)
        const actions = {
          "None": {
            action_key: "None",
            script_path: "",
            description: "No action",
            needsUserInput: false
          },
          ...transformDynamoDBToActions(items)
        };

        setMappings(actions);
      } catch (err) {
        console.error('Error fetching script mappings from DynamoDB:', err);
        setError({
          message: 'Failed to load script mappings from database',
          originalError: err
        });
        setMappings(null);
      } finally {
        setLoading(false);
      }
    };

    fetchMappings();
  }, []);

  return { mappings, loading, error };
}
